import type { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import type { ParsedPushNotificationPayload } from '@hunting-nature/shared-core/interfaces/push-notification-payload.interface';
import { createAction, props } from '@ngrx/store';

import { prefixed } from '../../utils/factory.utils';

const push = prefixed('[Push]');

/**
 * Why push never started. Deliberately not a permission value: the OS was never asked, so
 * whatever it answered before — including "not asked yet" — still stands.
 */
export type PushSkipReason = 'disabled' | 'unsupported';

export const PushActions = {
  /**
   * Entry point. Dispatched after a successful login and on app resume — never from the
   * app initializer, because registering needs a bearer token the initializer does not
   * have yet.
   */
  initPush: createAction(push('Init push')),
  /** Environment has push disabled, or the platform is web. Nothing to do. */
  initPushSkipped: createAction(push('Init push skipped'), props<{ reason: PushSkipReason }>()),

  /**
   * The per-install id is read from Preferences (or generated) here, early, so that the
   * logout path can pick it up synchronously from the store — an async storage read at
   * that point would race the auth tokens being cleared.
   */
  installationIdResolved: createAction(push('Installation id resolved'), props<{ installationId: string }>()),

  permissionGranted: createAction(push('Permission granted')),
  permissionDenied: createAction(push('Permission denied')),

  /** The OS handed us a native handle (APNs device token / FCM registration token). */
  tokenReceived: createAction(push('Token received'), props<{ token: string }>()),
  /** APNs or FCM refused to issue a token — offline, missing entitlement, no google-services.json. */
  tokenFailed: createAction(push('Token failed'), props<{ error: unknown }>()),

  registerDevice: createAction(push('Register device'), props<{ token: string }>()),
  registerDeviceSucceeded: createAction(push('Register device succeeded'), props<{ installationId: string; token: string }>()),
  registerDeviceFailed: createAction(push('Register device failed'), props<{ error: unknown }>()),

  /** Dispatched on logout, before the auth tokens are cleared. */
  unregisterDevice: createAction(push('Unregister device')),
  unregisterDeviceSucceeded: createAction(push('Unregister device succeeded')),
  unregisterDeviceFailed: createAction(push('Unregister device failed'), props<{ error: unknown }>()),

  notificationReceived: createAction(
    push('Notification received'),
    props<{ notification: PushNotificationSchema; payload: ParsedPushNotificationPayload | null }>()
  ),
  notificationActionPerformed: createAction(
    push('Notification action performed'),
    props<{ action: ActionPerformed; payload: ParsedPushNotificationPayload | null }>()
  ),
  clearPendingNotificationIntent: createAction(push('Clear pending notification intent')),

  /** Dispatched when the user changes the sound preference in settings. Triggers re-registration with the new preference. */
  soundPreferenceChanged: createAction(push('Sound preference changed'), props<{ sound: 'default' | 'secacam' }>()),
};
